import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import Collapse from 'bootstrap/js/dist/collapse';
import { FormArray, FormGroup, AbstractControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { ClinicalTrialsUIService } from '../../services/clinical-trials-ui.service';
import { CRITERIA_CODE_DISPLAY_VALUES, CRITERIA_FILTERS } from '../../constants';
import { ExpanderDirective } from '../../directives/expander.directive';
import { ICriteria } from '../../interfaces';
import { IItemAnswerCode } from '../../types';
import * as utils from '../../utils';

@UntilDestroy()
@Component({
  selector: '[appCriteriaForm]',
  styleUrls: ['./criteria-form.component.scss'],
  templateUrl: './criteria-form.component.html'
})
export class CriteriaFormComponent extends ExpanderDirective implements AfterViewInit, OnDestroy, OnInit {

  @Input()
  criteria: ICriteria;

  @Input()
  groupId: string;

  criteriaFilters = {...CRITERIA_FILTERS};
  form: FormGroup;
  name: string;

  get criteriaMatched(): number {
    return utils.getCriteriaCountByCriteriaType(this.form.value as fhir4.QuestionnaireResponse, this.criteria.criteriaMatchType);
  }

  get criteriaNotMatched(): number {
    return utils.getCriteriaCountByCriteriaType(this.form.value as fhir4.QuestionnaireResponse, this.criteria.criteriaNoMatchType);
  }

  get criteriaUnknown(): number {
    return utils.getCriteriaCountByCriteriaType(this.form.value as fhir4.QuestionnaireResponse, 'unknown');
  }

  get itemFormArray(): FormArray {
    return this.form.get('item') as FormArray;
  }

  constructor(
    private readonly clinicalTrialsUIService: ClinicalTrialsUIService
  ) {
    super();
  }

  ngOnInit(): void {
    this.expanderSelector = `#collapse-${ this.criteria.expanderID }`;

    if (this.criteria.criteriaLabels.length) {

      this.buildForm();

      this.itemFormArray.controls.forEach(item => {
        const answerControl = (item.get('answer') as FormArray).controls[0];

        answerControl.get('valueCoding.code').valueChanges.pipe(untilDestroyed(this)).subscribe(value => {
          const displayValue = CRITERIA_CODE_DISPLAY_VALUES[value];

          answerControl.get('valueCoding.display').patchValue(displayValue);
          this.criteria.criteriaMatched = this.criteriaMatched;
          this.criteria.criteriaNotMatched = this.criteriaNotMatched;
          this.criteria.criteriaUnknown = this.criteriaUnknown;
          this.clinicalTrialsUIService.onCriteriaChanged(this.form.value.item);
        });
      });
    }
  }

  buildForm(): void {
    this.form = new FormGroup({
      item: new FormArray(this.criteria.criteriaLabels.map(label => {
        const questionnaireResponseItem: fhir4.QuestionnaireResponseItem = this.criteria.criteriaAnswers?.find(item => item.linkId === label.linkId);

        return utils.initItemForm(label.linkId, questionnaireResponseItem);
      }))
    });

    this.criteria.criteriaMatched = this.criteriaMatched;
    this.criteria.criteriaNotMatched = this.criteriaNotMatched;
    this.criteria.criteriaUnknown = this.criteriaUnknown;
  }

  getCriteriaCode(itemForm: AbstractControl): IItemAnswerCode {
    return (itemForm.get('answer') as FormArray).controls[0].get('valueCoding.code').value as IItemAnswerCode;
  }

  toggleBadge(event: Event, criteriaType: IItemAnswerCode): void {
    this.criteriaFilters[criteriaType] = !this.criteriaFilters[criteriaType];
    this.expanderInstances[0].show();
    this.stopPropagation(event);
  }
}
